/*
 * Indigo2 stream message types as specified in https://confluence.dev.instinet.com/display/IN2/Websocket+Stream+Status+Messages
 */
import { WellKnownWebsocketErrorCodes } from 'communication/streaming/SockJsStreamingErrorCodes';
import capitalize from 'lodash/capitalize';

export enum WellKnownIndigo2MessageType {
    ERROR = 'ERROR',
    INFO = 'INFO',
    AUTHORITY = 'AUTHORITY',
    AVAILABILITY_STATUS = 'AVAILABILITY_STATUS',
}

// See https://confluence.dev.instinet.com/display/IN2/Indigo+Session+Resiliance+Proposal for these definitions...
export type WellKnownIndigo2AvailabilityStatus =
    //The availability status value has not yet been initialised on the Indigo 2 server. This status should not persist for more than a second or so after the server restarts.
    | 'NOT_SET'
    // The Indigo 1 backend server is running but not yet ready to receive requests (e.g. because it is completing a restart)
    | 'INDIGO1_NOT_READY'
    // The Indigo 1 backend server could not be reached and is probably not running on the Indigo 1 url configured in Indigo 2.
    | 'INDIGO1_NOT_REACHABLE'
    // The Indigo 1 availability status could not be determined.
    | 'INDIGO1_NOT_KNOWN'
    // An error was encountered when trying to access the Indigo 1 availability status.
    | 'INDIGO1_ERROR'
    // The Indigo 2 server is running but not ready to accept requests
    | 'INDIGO2_NOT_READY'
    // No websocket connection to the Indigo 2 server could be established.
    // This will never be sent as the status value in an AVAILABILITY_STATUS websocket message, for obvious reasons. Instead, the Indigo frontend should implicitly assume this availability status value whenever an active websocket connection to the Indigo 2 server is not present.
    // Note Indigo 2 also provides an unauthenticated '/Indigo2/availabilityStatus/' rest endpoint, which returns the same response object as the AVAILABILITY_STATUS websocket message payload. This could also be used by the frontend to test for Indigo 2 availability independently of being able to establish a websocket connection.
    | 'INDIGO2_NOT_REACHABLE'

    // The full Indigo server-side stack is available and ready to accept requests
    | 'READY'
    // Future functionality
    // Functionally the same as READY, but would give notice that an Indigo server restart is scheduled to start imminently.
    | 'READY_RESTART_PENDING'
    // Future functionality
    // Instructs the frontend to display a 'holding page'
    | 'HOLDING_PAGE';

export const availabilyStatusDesc = (status?: string) => {
    return status ? capitalize(status.toLowerCase().replace(/_/g, ' ')) : '';
};

export interface StreamStatusMessageErrorPayload {
    status: string;
    code: WellKnownWebsocketErrorCodes;
    reason: string;
    exceptionType?: string;
    exceptionMessage?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    errorDetail?: any;
}

export interface Indigo2StreamStatusMessage {
    source: 'Indigo2';
}

export interface StreamStatusMessageError extends Indigo2StreamStatusMessage {
    type: WellKnownIndigo2MessageType.ERROR;
    payload: StreamStatusMessageErrorPayload;
}

export enum StreamStatusMessageInfoCodes {
    Logout = 4201,
}

export interface StreamStatusMessageInfo extends Indigo2StreamStatusMessage {
    type: WellKnownIndigo2MessageType.INFO;
    payload: {
        status: string;
        code: StreamStatusMessageInfoCodes;
        reason: string;
    };
}

export interface StreamStatusMessageAuthority
    extends Indigo2StreamStatusMessage {
    type: WellKnownIndigo2MessageType.AUTHORITY;
    payload: {
        authorities: string[];
        enabled: boolean;
    };
}

export interface StreamStatusMessageAvailabilityStatus
    extends Indigo2StreamStatusMessage {
    type: WellKnownIndigo2MessageType.AVAILABILITY_STATUS;
    payload: {
        status: WellKnownIndigo2AvailabilityStatus;
    };
}

export type StreamStatusMessage =
    | StreamStatusMessageError
    | StreamStatusMessageInfo
    | StreamStatusMessageAuthority
    | StreamStatusMessageAvailabilityStatus;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isStreamStatusMessage(r?: any): r is StreamStatusMessage {
    return !!r && (r as StreamStatusMessage).source === 'Indigo2';
}
